import { defineFormConfig } from "@src/components/business-form";
import type {
    StockKanbanCreateContentParams,
    StockKanbanUpdateContentParams,
} from "../../../../../../api/stockKanbanContainers";
import type { StockKanbanSupplyAvailableLotOption } from "../../helpers/loadStockKanbanSupplyLotAvailability";
import { resolveStockKanbanSupplyContentMaxQty } from "../../helpers/resolveStockKanbanSupplyContentAvailableQty";
import { isStockKanbanSupplyCellSelectionPending } from "../../helpers/resolveStockKanbanSupplyLotPlacementMode";

/** Значения формы партии контейнера КАНБАН. */
export interface StockKanbanSupplyContentFormValues {
    /**
     * Выбранная партия вместе с остатками по ячейкам склада-отправителя
     * (опция доменного селекта — см. `StockKanbanSupplyAvailableLotSearchSelect`). \
     * В доменный маппинг уходит только `LotCode`; `null` — партия не выбрана.
     */
    lot: StockKanbanSupplyAvailableLotOption | null;
    /** Количество отпуска. */
    qty: number;
    /**
     * Код ячейки хранения партии. \
     * `null` — ещё не выбрана (режим нескольких ячеек); `0` — «на полу».
     */
    spCode: number | null;
}

/** Контекст формы партии контейнера КАНБАН. */
export interface StockKanbanSupplyContentFormContext {
    /** Код контейнера/объекта системы. */
    containerCode: number;
    /** Код редактируемой строки партии. */
    contentCode?: number;
    /**
     * Свободная ёмкость контейнера для поля qty: \
     * `container.Qty − Σ qty остальных строк состава` (без текущей при edit).
     */
    containerCapacityMax: number;
}

/** Конфигурация формы партии контейнера КАНБАН. */
export const stockKanbanSupplyContentFormConfig = defineFormConfig<
    StockKanbanSupplyContentFormValues,
    StockKanbanCreateContentParams | StockKanbanUpdateContentParams,
    StockKanbanSupplyContentFormContext
>({
    fields: {
        lot: {
            type: "object",
            nullable: true,
            label: "Партия",
            validation: {
                required: true,
                /** При смене партии пересчитываем лимит qty. */
                deps: ["qty"],
            },
            map: {
                /** Домен отдаёт только код; обогащённую опцию резолвит форма при открытии. */
                from: () => null,
                to: (value, _values, ctx) => ({
                    containerCode: ctx?.containerCode ?? 0,
                    contentCode: ctx?.contentCode,
                    lotCode: value?.LotCode ?? 0,
                }),
            },
        },
        qty: {
            type: "number",
            label: "Количество собранного",
            /**
             * Ввод qty недоступен, пока не выбрана партия,
             * либо пока не выбрана ячейка для партии, размещённой в ячейках (одной или нескольких).
             */
            disabled: (values) => values.lot == null || isStockKanbanSupplyCellSelectionPending(values.lot, values.spCode),
            description: (values, ctx) => {
                const maxQty = resolveStockKanbanSupplyContentMaxQty(
                    values.lot,
                    values.spCode,
                    ctx?.containerCapacityMax ?? 0,
                );
                return maxQty == null ? "" : `Макс.: ${maxQty}`;
            },
            validation: {
                required: true,
                positive: true,
                max: (values, ctx) => {
                    const maxQty = resolveStockKanbanSupplyContentMaxQty(
                        values.lot,
                        values.spCode,
                        ctx?.containerCapacityMax ?? 0,
                    );
                    if (maxQty == null) {
                        return false;
                    }

                    return {
                        value: maxQty,
                        message: `Не больше ${maxQty}`,
                    };
                },
            },
            map: "qty",
        },
        spCode: {
            type: "number",
            nullable: true,
            label: "Ячейка хранения",
            /**
             * `null` — не выбрано (multi); `0` — «на полу» и проходит required. \
             * Ошибка поля останавливает submit до `onSubmit` (окно не закрывается через `closeOnSubmit`).
             */
            validation: {
                required: true,
                /** При смене ячейки пересчитываем лимит qty (multi: min остатка ячейки и остатка партии). */
                deps: ["qty"],
            },
            map: {
                from: (domain) => domain.spCode,
                to: (value) => ({
                    spCode: value ?? 0,
                }),
            },
        },
    },
});
